import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { BrandMark } from '@/components/BrandLogo';
import SiteLegalLinks from '@/components/SiteLegalLinks';
import {
  OPERATOR_LEGAL_NAME,
  OPERATOR_COMPANY_NUMBER,
  PRODUCT_NAME,
} from '@/legal/companyInfo';
import { toast } from 'sonner';
import adminRestaurantBackground from '@/assets/admin-restaurant-background.png';
import { formatRateLimitMessage } from '@/utils/rateLimitMessage';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, isAuthenticated, user, loading: authLoading, isTenantUser, pinPending } = useAuth();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  useEffect(() => {
    if (authLoading) return;
    if (pinPending) { navigate('/admin/pin-login', { replace: true }); return; }
    if (!isAuthenticated || !user) return;
    if (user.role === 'platform_admin') navigate('/platform/dashboard', { replace: true });
    else if (isTenantUser) navigate('/admin/dashboard', { replace: true });
  }, [authLoading, isAuthenticated, user, isTenantUser, pinPending, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      if (data?.requires_pin) {
        toast.success('Enter your 4-digit PIN to continue');
        navigate('/admin/pin-login');
      } else {
        toast.success('Welcome back!');
        if (data?.user?.role === 'platform_admin') navigate('/platform/dashboard');
        else navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error(formatRateLimitMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <img src={adminRestaurantBackground} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/78 to-slate-950/35" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_48%_36%,rgba(245,158,11,0.18),transparent_34%)]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-slate-950/90 to-transparent" />

      <div className="relative z-10 p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>

      <div className="relative z-10 flex min-h-[calc(100vh-88px)] items-center justify-center px-6 pb-10">
        <div className="grid w-full max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_440px]">

          {/* Left panel */}
          <section className="hidden animate-scale-in text-white lg:block">
            <BrandMark size="lg" className="mb-8 border-white/25 shadow-2xl shadow-amber-950/40 backdrop-blur-md" />
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.35em] text-amber-100/80">Restaurant Console</p>
            <h1 className="max-w-2xl font-heading text-5xl font-bold leading-tight tracking-tight text-white">
              A calmer way to run a busy dining room.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-white/72">
              Manage bookings, deposits, guest details, and service flow from one premium workspace built for restaurants.
            </p>
            <div className="mt-10 grid max-w-xl gap-4 sm:grid-cols-3">
              {['Live booking control', 'Deposit tracking', 'Calendar sync'].map((item) => (
                <div key={item} className="rounded-2xl border border-white/15 bg-white/10 p-4 text-sm text-white/80 backdrop-blur-md">
                  <CheckCircle2 className="mb-3 h-4 w-4 text-amber-200" />
                  {item}
                </div>
              ))}
            </div>
          </section>

          {/* Right panel */}
          <div className="w-full animate-scale-in">
            <div className="mb-6 text-center lg:hidden">
              <BrandMark size="lg" className="mx-auto mb-5 border-white/25 shadow-2xl shadow-amber-950/40 backdrop-blur-md" />
            </div>

            <Card className="border-white/15 bg-white/95 text-slate-950 shadow-2xl shadow-black/40 backdrop-blur-xl">
              <CardHeader className="space-y-1 pb-4">
                <h2 className="font-heading text-2xl font-semibold tracking-tight">Welcome back</h2>
                <p className="text-sm text-muted-foreground">Sign in to manage today's service.</p>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@restaurant.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      data-testid="login-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required
                      data-testid="login-password"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <footer className="relative z-10 border-t border-white/10 px-6 py-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4">
          <SiteLegalLinks variant="dark" />
          <p className="text-center text-xs text-white/50">
            © {new Date().getFullYear()} {PRODUCT_NAME} · {OPERATOR_LEGAL_NAME} · Co. no. {OPERATOR_COMPANY_NUMBER}
          </p>
        </div>
      </footer>
    </div>
  );
}
